import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../../../components/common/PageHeader";
import { VERTICALS } from "../../../../constants/verticals";
import { useAuth } from "../../../../context/AuthContext";
import { getRecycleBinEnquiries, restoreInquiry } from "../api/inquiry.api";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "../styles/inquiry.css";
import { getFixedDropdownPosition } from "../../../../components/common/dropdownPositionUtils";

interface RecycleBinEnquiry {
  id: number;
  vertical?: number;
  customer_name?: string | null;
  contact_person_name?: string | null;
  enquiry_stage_name?: string | null;
  probability_name?: string | null;
  owner_name?: string | null;
  assigned_to_name?: string | null;
  client_segment_name?: string | null;
  vertical_name?: string | null;
  date_updated?: string | null;
  contact_details?: {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
}

const InquiryRecycleBin: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin =
    user?.role === "Admin" || user?.role === "CompanyAdmin";
  const canAccessRecycleBin = isAdmin || user?.role === "Manager";

  const [list, setList] = useState<RecycleBinEnquiry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openActionId, setOpenActionId] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const inquiriesPath = isAdmin
    ? `/${VERTICALS[0].key}/inquiries`
    : `/${user?.vertical_key}/inquiries`;

  useEffect(() => {
    const close = () => setOpenActionId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const fetchRecycleBin = async () => {
    try {
      setLoading(true);
      const params = isAdmin ? undefined : { vertical: user?.vertical_id };
      const res = await getRecycleBinEnquiries(params);
      setList(res.data?.data || []);
      setTotalCount(res.data?.count ?? res.data?.data?.length ?? 0);
    } catch {
      toast.error("Failed to load recycle bin enquiries");
      setList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccessRecycleBin) return;
    fetchRecycleBin();
  }, [canAccessRecycleBin, isAdmin, user?.vertical_id]);

  if (!canAccessRecycleBin) {
    return <Navigate to="/dashboard" replace />;
  }

  const formatDate = (date?: string | null) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getClientName = (row: RecycleBinEnquiry) =>
    row.customer_name ||
    row.contact_details?.name ||
    row.contact_person_name ||
    "-";

  const getContactDetails = (row: RecycleBinEnquiry) => {
    const phone = row.contact_details?.phone;
    const email = row.contact_details?.email;
    if (phone && email) return `${phone} / ${email}`;
    return phone || email || "-";
  };

  const getVerticalKey = (row: RecycleBinEnquiry) => {
    const match = VERTICALS.find((v) => v.id === row.vertical);
    return match?.key || user?.vertical_key || VERTICALS[0].key;
  };

  const selectedRow = list.find((row) => row.id === openActionId);

  const handleRestoreInquiry = async (id: number) => {
    const result = await Swal.fire({
      title: "Restore enquiry?",
      text: "This enquiry will be moved back to the active inquiry list.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, restore",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await restoreInquiry(id);
      toast.success(res.data?.message || "Enquiry restored successfully");
      setOpenActionId(null);
      fetchRecycleBin();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to restore enquiry"
      );
    }
  };

  return (
    <>
      <PageHeader
        title="Recycle Bin"
        breadcrumbs={[
          { label: "Home" },
          { label: "Sales" },
          { label: "Recycle Bin" },
        ]}
      />

      <div className="container mt-4">
        <div className="info-card">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">Deleted Enquiries</h4>
              <p className="text-muted mb-0 recycle-bin-subtitle">
                Enquiries moved to the recycle bin are listed here.
              </p>
            </div>
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate(inquiriesPath)}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Inquiries
            </button>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="inquiry-table-wrapper table-responsive">
              <table className="table inquiry-table align-middle">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>Action</th>
                    <th>ID</th>
                    {isAdmin && <th>Vertical</th>}
                    <th>Client Name</th>
                    <th>Contact Details</th>
                    <th>Client Segment</th>
                    <th>Stage</th>
                    <th>Probability</th>
                    <th>Assigned To</th>
                    <th>Owner</th>
                    <th>Deleted On</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? 11 : 10} className="text-center py-4">
                        No enquiries in recycle bin
                      </td>
                    </tr>
                  ) : (
                    list.map((row) => (
                      <tr key={row.id}>
                        <td style={{ textAlign: "center", position: "relative" }}>
                          <button
                            className="inquiry-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDropdownPos(
                                getFixedDropdownPosition(rect, { itemCount: 2 })
                              );
                              setOpenActionId(
                                openActionId === row.id ? null : row.id
                              );
                            }}
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                        </td>
                        <td>{row.id}</td>
                        {isAdmin && <td>{row.vertical_name || "-"}</td>}
                        <td>{getClientName(row)}</td>
                        <td>{getContactDetails(row)}</td>
                        <td>{row.client_segment_name || "-"}</td>
                        <td>{row.enquiry_stage_name || "-"}</td>
                        <td>{row.probability_name || "-"}</td>
                        <td>{row.assigned_to_name || "-"}</td>
                        <td>{row.owner_name || "-"}</td>
                        <td style={{ textAlign: "center" }}>
                          {formatDate(row.date_updated)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {openActionId && selectedRow && (
                <div
                  className="inquiry-action-dropdown fixed"
                  style={{
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => {
                      navigate(
                        `/${getVerticalKey(selectedRow)}/inquiries/${openActionId}/view`
                      );
                      setOpenActionId(null);
                    }}
                  >
                    View
                  </button>
                  <button
                    className="restore"
                    onClick={() => handleRestoreInquiry(openActionId)}
                  >
                    Restore
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && list.length > 0 && (
            <div className="text-muted mt-3 recycle-bin-count">
              Total: {totalCount} {totalCount === 1 ? "enquiry" : "enquiries"}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InquiryRecycleBin;
